// Cubic-bezier easing applied to a clip-boundary transition's raw linear progress (see
// getTransitionAt in compositor/transitions.js) - the same evaluation approach browsers use for
// CSS's own cubic-bezier() timing functions: solve for t given the desired x (the transition's
// raw 0-1 progress), then evaluate y(t) as the actual eased progress fed into blendTransition.
// Control points are stored as a flat [x1, y1, x2, y2] array - P0=(0,0) and P3=(1,1) are always
// fixed (a transition must start at 0 and finish at 1), only the two middle handles are editable.
const bezierComponent = (t, p1, p2) => {
    const c = 3 * p1
    const b = 3 * (p2 - p1) - c
    const a = 1 - c - b
    return ((a * t + b) * t + c) * t
}

const bezierComponentDerivative = (t, p1, p2) => {
    const c = 3 * p1
    const b = 3 * (p2 - p1) - c
    const a = 1 - c - b
    return (3 * a * t + 2 * b) * t + c
}

export const DEFAULT_EASING_POINTS = [0, 0, 1, 1]

export const EASING_PRESETS = [
    { key: 'linear', labelKey: 'dash.post-creator.media-editor.clip-tool.easing-linear', points: [0, 0, 1, 1] },
    { key: 'ease', labelKey: 'dash.post-creator.media-editor.clip-tool.easing-ease', points: [0.25, 0.1, 0.25, 1] },
    { key: 'ease-in', labelKey: 'dash.post-creator.media-editor.clip-tool.easing-ease-in', points: [0.42, 0, 1, 1] },
    { key: 'ease-out', labelKey: 'dash.post-creator.media-editor.clip-tool.easing-ease-out', points: [0, 0, 0.58, 1] },
    { key: 'ease-in-out', labelKey: 'dash.post-creator.media-editor.clip-tool.easing-ease-in-out', points: [0.42, 0, 0.58, 1] }
]

// Given the transition's raw linear progress (0-1) and its 4 control-point values, returns the
// eased progress actually used to blend the two clips. x1/x2 stay solvable via Newton-Raphson
// (starting from t = progress converges in a handful of iterations for any valid, monotonic-x
// curve); when both handles share the same x/y values the curve's x(t) and y(t) are identical
// polynomials, so y(t) === progress by construction and solving can be skipped entirely.
export const applyEasing = (progress, points) => {
    const [x1, y1, x2, y2] = points && points.length === 4 ? points : DEFAULT_EASING_POINTS
    const clampedProgress = Math.min(Math.max(progress, 0), 1)
    if (x1 === y1 && x2 === y2) return clampedProgress

    let t = clampedProgress
    for (let i = 0; i < 8; i++) {
        const x = bezierComponent(t, x1, x2) - clampedProgress
        const dx = bezierComponentDerivative(t, x1, x2)
        if (Math.abs(dx) < 1e-6) break
        t = Math.min(Math.max(t - x / dx, 0), 1)
    }
    return bezierComponent(t, y1, y2)
}

// Which preset (if any) the given points match, within a small tolerance - lets the UI highlight
// the right preset button (rather than always defaulting to "Personnalisée") after a curve that
// happens to land back on a "nice" shape, e.g. right after switching transition type resets it.
export const matchEasingPreset = (points) => {
    if (!points || points.length !== 4) return 'linear'
    const match = EASING_PRESETS.find(preset => preset.points.every((v, i) => Math.abs(v - points[i]) < 0.01))
    return match ? match.key : 'custom'
}
